import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import classNames from 'classnames'
import { Select } from 'antd'
import { FormattedMessage } from '~/translations/utils'
import { DatePicker } from 'antd'
import { UseFormSetValue, FieldErrors, Control } from 'react-hook-form'
import { FormValues } from '../AddTicket'
import { TicketType } from '~/components/tickets/Show'
import {
  TextField,
  TextAreaField,
  FieldController as Field,
} from '~/components/common/Form'

type Props = {
  ticket: TicketType | null
  control: Control<any>
  setValue: UseFormSetValue<any>
  tab?: string | null
  edit?: boolean
}

const { Option } = Select

const TicketForm: React.FC<Props> = ({
  ticket,
  control,
  tab,
  setValue,
  edit,
}: Props) => {
  // const DatePicker = generatePicker(dateFnsGenerateConfig)

  const user = window.c.user
  const users = window.c.users.filter(user => !user.removed && !user.guest)
  const reportedBy = window.c.ticketReportTypes
  const mailAccounts: MaillAccount[] = window.c.mailAccounts
  const ticketCategories = window.c.ticketCategories.filter(
    category => !category.readOnly
  )

  const [subcategories, setSubcategories] = useState<Category[]>(() => {
    return ticket && ticket.category
      ? ticketCategories.find(item => item.id == ticket.category?.id)!
          .subcategories
      : []
  })

  type MaillAccount = {
    id: number
    address: string
  }

  type Category = {
    id: number
    name: string
    deleted: boolean
  }

  const changeCategory = e => {
    setValue('ticketCategory', e)

    if (e) {
      const categories = ticketCategories.find(
        item => item.id == e
      )!.subcategories

      setSubcategories(categories)

      !categories.length && setValue('ticketSubcategory', null)
    } else {
      setSubcategories([])
      setValue('ticketSubcategory', null)
    }
  }

  return (
    <>
      <Controller
        name={'title'}
        control={control}
        defaultValue={''}
        render={renderProps => (
          <TextField
            label={intl.formatMessage({
              id: 'ticket / add / title',
            })}
            renderProps={renderProps}
            horizontal={false}
            small={true}
          />
        )}
      />

      {!edit && (
        <Controller
          name={'content'}
          control={control}
          defaultValue={''}
          render={renderProps => (
            <TextAreaField
              label={intl.formatMessage({
                id: 'ticket / add / content',
              })}
              renderProps={renderProps}
              horizontal={false}
              small={true}
            />
          )}
          rules={{
            required: intl.formatMessage({
              id: 'ticket / validate / error / empty content',
            }),
          }}
        />
      )}

      <Controller
        name="deadline"
        control={control}
        render={({ field }) => (
          <Field
            label={intl.formatMessage({
              id: 'ticket / add / deadline',
            })}
          >
            <DatePicker {...field} className={'full-width'} />
          </Field>
        )}
      />

      {mailAccounts.length && !edit && tab != 'task' ? (
        <Controller
          name="mailAccount"
          control={control}
          defaultValue={mailAccounts[0].id}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'tickets / view / filter / email',
              })}
            >
              <Select
                {...field}
                className={'full-width'}
                defaultValue={mailAccounts[0].id}
              >
                {mailAccounts.map((account, index) => (
                  <Option key={index} value={account?.id}>
                    {account?.address}
                  </Option>
                ))}
              </Select>
            </Field>
          )}
        />
      ) : null}

      {ticketCategories.length && (tab != 'task' || edit) ? (
        <Controller
          name="ticketCategory"
          control={control}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'ticket / add / category',
              })}
            >
              <Select
                {...field}
                onChange={e => changeCategory(e)}
                className={'full-width'}
                placeholder={intl.formatMessage({
                  id: 'ticket / add / category / select',
                })}
                allowClear={true}
              >
                {ticketCategories
                  .filter(category => !category.deleted)
                  .map((category, index) => (
                    <Option key={index} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
              </Select>
            </Field>
          )}
        />
      ) : null}

      {subcategories.length && (tab != 'task' || edit) ? (
        <Controller
          name="ticketSubcategory"
          control={control}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'ticket / add / subcategory',
              })}
            >
              <Select
                {...field}
                className={'full-width'}
                placeholder={intl.formatMessage({
                  id: 'ticket / add / subcategory / select',
                })}
              >
                {subcategories
                  .filter(category => !category.deleted)
                  .map((category, index) => (
                    <Option key={index} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
              </Select>
            </Field>
          )}
        />
      ) : null}

      {!edit && (
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'ticket / add / tags',
              })}
            >
              <Select
                {...field}
                mode="tags"
                className={'full-width'}
                notFoundContent={null}
              />
            </Field>
          )}
        />
      )}

      {tab != 'task' && !edit && (
        <Controller
          name="reportedBy"
          control={control}
          defaultValue={'1'}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'ticket / add / reportedBy',
              })}
            >
              <Select {...field} className={'full-width'} defaultValue={'1'}>
                {Object.entries(reportedBy).map(([key, item]: any) => (
                  <Option key={key} value={key}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Field>
          )}
        />
      )}

      {!edit && (
        <Controller
          name="assignTo"
          control={control}
          defaultValue={[user.id]}
          render={({ field }) => (
            <Field
              label={intl.formatMessage({
                id: 'ticket / add / assignTo',
              })}
            >
              <Select
                {...field}
                mode="multiple"
                className={'full-width'}
                defaultValue={[user.id]}
              >
                {users.map(item => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Field>
          )}
        />
      )}
    </>
  )
}

export default TicketForm
